import React, { useEffect, useRef, useState } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import { useHelpPanelLink } from '@atlassian/jira-help-panel-button/src/index.tsx';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/jira-product-analytics-bridge';
import { isSyntheticTenant } from '@atlassian/jira-proforma-utils/src/common/utils/is-synthetic-tenant/index.tsx';
import { toProformaEnvironment } from '@atlassian/jira-proforma-utils/src/common/utils/to-proforma-environment/index.tsx';
import { getUrls, ANALYTICS_CHANNEL } from '@atlassian/jira-proforma-utils/src/index.tsx';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { JsmPageInteractiveEvent } from '@atlassian/jira-servicedesk-common/src/utils/browser-metric/index.tsx';
import { SpaStatePageReady } from '@atlassian/jira-spa-state-controller/src/components/main.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import type { BackendSettings } from '@atlassian/proforma-common-core/jira-common-models';
import {
	JiraProjectFormsApp,
	type JiraProjectFormsSettings,
} from '@atlassian/proforma-jira-project-forms';
import { TemplateLibraryContainer } from '@atlassian/proforma-template-library-store';
import { AsyncBenefitModal } from './benefit-modal/index.tsx';
import { FormSettingsTemplateLibrary } from './template-library/index.tsx';

const ProformaProjectFormsSettings = () => {
	const { locale } = useIntl();
	const { data: projectContext } = useProjectContext();
	const { projectId, projectKey, projectType, isProjectAdmin, simplified } = projectContext ?? {};
	const cloudId = useCloudId();
	const environment = useEnvironment();
	const onOpenInProductHelpArticle = useRef(useHelpPanelLink());
	const [settings, setSettings] = useState<BackendSettings<JiraProjectFormsSettings>>();

	useEffect(() => {
		if (projectId && projectKey && projectType && simplified !== undefined) {
			setSettings({
				analytics: { channel: ANALYTICS_CHANNEL },
				flags: {},
				urls: getUrls(environment, cloudId),
				environment: toProformaEnvironment(environment),
				context: {
					projectId,
					projectKey,
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					projectType: projectType as 'business' | 'service_desk' | 'software',
					userIsProjectAdmin: isProjectAdmin,
					inProductHelpData: {
						simplifiedProject: simplified,
						onOpenInProductHelpArticle: (articleId: string) => {
							if (onOpenInProductHelpArticle.current) {
								onOpenInProductHelpArticle.current(articleId);
							}
						},
					},
					intlLocale: locale,
				},
			});
		}
	}, [
		environment,
		cloudId,
		projectId,
		isProjectAdmin,
		projectType,
		simplified,
		onOpenInProductHelpArticle,
		locale,
		projectKey,
	]);

	return projectContext !== null && projectId && settings ? (
		<ContextualAnalyticsData
			sourceName="proformaJiraProjectForms"
			sourceType={SCREEN}
			attributes={{
				projectId,
				projectType,
				isSimplified: simplified,
			}}
		>
			<TemplateLibraryContainer>
				<JiraProjectFormsApp settings={settings} />
				<FormSettingsTemplateLibrary projectId={projectId} />
			</TemplateLibraryContainer>
			<JsmPageInteractiveEvent />
			{!isSyntheticTenant() && <AsyncBenefitModal />}
			{/* SpaStatePageReady is used to indicate that the app can move to the "afterPaint" phase in react-loosely-lazy. */}
			{fg('jsm_simplified_project_settings_navigation') && <SpaStatePageReady />}
		</ContextualAnalyticsData>
	) : null;
};

export default ProformaProjectFormsSettings;
